// src/pages/ClinicalDataCollector/components/output/MedicationDetailTable.jsx
import React, { useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  Card,
  CardHeader,
  CardContent,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Typography,
  Divider,
  Box,
  Tooltip,
  CircularProgress,
  Alert,
} from '@mui/material';
import LocalPharmacyIcon from '@mui/icons-material/LocalPharmacy';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import SyncProblemIcon from '@mui/icons-material/SyncProblem';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import { buildMedicationDetailContextText } from '../../../../utils/clinicalNotesFormatter';
import { generateMedicationDetailFromAI } from '../../../../utils/services/medicationAiService';

const MedicationDetailTable = ({
  medications = [],
  allergies = [],
  annualWellnessNotes = [],
  followUpExamNotes = [],
  telehealthNotes = [],
  hospitalDischargeNotes = [],
}) => {
  // Solo usamos Redux para saber qué paciente / id está activo
  const { PatientSelect, advancedMdPatientId } = useSelector(
    (state) => state.patientMapSelect || {}
  );

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const patientName =
    PatientSelect &&
    (PatientSelect.fullName ||
      [PatientSelect.firstName, PatientSelect.lastName]
        .filter(Boolean)
        .join(' '));

  // 🔹 Construimos el contexto para IA a partir de props
  const contextText = useMemo(
    () =>
      buildMedicationDetailContextText({
        annualWellnessNotes,
        followUpExamNotes,
        telehealthNotes,
        hospitalDischargeNotes,
        medications,
        allergies,
      }),
    [
      annualWellnessNotes,
      followUpExamNotes,
      telehealthNotes,
      hospitalDischargeNotes,
      medications,
      allergies,
    ]
  );

  useEffect(() => {
    // Si no hay id de AdvancedMD o no hay contexto, no llamamos IA
    if (!advancedMdPatientId) {
      setRows([]);
      return;
    }
    if (!contextText || !contextText.trim()) {
      setRows([]);
      return;
    }

    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        setErrorMsg('');
        const medsFromAi = await generateMedicationDetailFromAI({
          contextText,
        });
        if (!cancelled) {
          setRows(medsFromAi || []);
        }
      } catch (e) {
        console.error('Error generating MedicationDetail from AI:', e);
        if (!cancelled) {
          setErrorMsg('Error while generating medication analysis from AI.');
          setRows([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [advancedMdPatientId, contextText]);

  const hasRows = rows && rows.length > 0;

  return (
    <Card elevation={3} sx={{ borderRadius: 2, mb: 4 }}>
      <CardHeader
        title="Detailed Medication Table"
        subheader={
          patientName
            ? `AI-powered summary for: ${patientName}`
            : 'AI-powered medication summary'
        }
        titleTypographyProps={{ variant: 'h5', align: 'center' }}
        action={<LocalPharmacyIcon fontSize="large" color="primary" />}
      />
      <Divider />
      <CardContent>
        {loading && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              py: 4,
            }}
          >
            <CircularProgress size={32} />
            <Typography sx={{ ml: 2 }}>
              Analyzing medications and clinical notes with AI...
            </Typography>
          </Box>
        )}

        {!loading && errorMsg && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMsg}
          </Alert>
        )}

        {!loading && !errorMsg && !hasRows && (
          <Typography variant="body2" color="text.secondary">
            No medication details available to display for this patient.
          </Typography>
        )}

        {!loading && !errorMsg && hasRows && (
          <>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Therapeutic Class</TableCell>
                  <TableCell>Prescriber</TableCell>
                  <TableCell>Last Fill</TableCell>
                  <TableCell>Next Refill</TableCell>
                  <TableCell align="center">Adherence</TableCell>
                  <TableCell>Interactions</TableCell>
                  <TableCell>BBW Warnings</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((med, idx) => (
                  <TableRow key={idx} hover>
                    <TableCell>{med.name}</TableCell>
                    <TableCell>{med.therapeuticClass}</TableCell>
                    <TableCell>{med.prescriber}</TableCell>
                    <TableCell>{med.lastFill}</TableCell>
                    <TableCell>{med.nextRefill}</TableCell>
                    <TableCell align="center">
                      <Tooltip
                        title={
                          med.adherenceReason ||
                          (med.adherence
                            ? 'Adherent to regimen'
                            : 'Possible non-adherence or refill gaps')
                        }
                      >
                        <Box component="span">
                          {med.adherence ? (
                            <CheckCircleIcon color="success" />
                          ) : (
                            <WarningAmberIcon color="warning" />
                          )}
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      {med.interactions && med.interactions.length > 0 ? (
                        <Box component="ul" sx={{ m: 0, pl: 2 }}>
                          {med.interactions.map((int, i) => (
                            <li key={i}>
                              <SyncProblemIcon
                                fontSize="small"
                                sx={{ verticalAlign: 'middle', mr: 0.5 }}
                              />
                              {int}
                            </li>
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          None noted
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {med.bbw && med.bbw.length > 0 ? (
                        <Box component="ul" sx={{ m: 0, pl: 2 }}>
                          {med.bbw.map((warn, i) => (
                            <li key={i}>
                              <ReportProblemIcon
                                fontSize="small"
                                sx={{ verticalAlign: 'middle', mr: 0.5 }}
                              />
                              {warn}
                            </li>
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          N/A
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Typography variant="caption" display="block" sx={{ mt: 2 }}>
              * Icons: ✔️ adherent, ⚠️ non-adherent risk; ↔️ interactions; ⚠️ BBW
              warning. Table generated from EHR meds, allergies and clinical
              notes using AI.
            </Typography>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default MedicationDetailTable;
